"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  NumberResult,
  type ResultLine,
} from "@/components/tools/numbers-languages/number-result";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type FrenchGender,
  type FrenchHyphenation,
  type FrenchNote,
  type FrenchRegion,
  frenchNumber,
  frenchOrdinal,
} from "@/lib/tool-kit/numbers-languages/french";
import {
  type DecimalNumber,
  type ReadError,
  readDecimalNumber,
} from "@/lib/tool-kit/numbers-languages/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const FRENCH_NUMBER_CONVERTER_STRINGS = {
  numberLabel: "Number",
  numberHint:
    "Whole numbers up to 24 digits; a decimal comma or point, a minus sign and spaces between digit groups are fine.",
  numberPlaceholder: "1999",
  modeLabel: "Spell it as",
  modeCardinal: "Cardinal (un, deux, trois)",
  modeOrdinal: "Ordinal (premier, deuxième)",
  regionLabel: "Regional form",
  regionFrance:
    "France and Canada (soixante-dix, quatre-vingts, quatre-vingt-dix)",
  regionBelgium:
    "Belgium, Geneva, Neuchâtel, Jura (septante, quatre-vingts, nonante)",
  regionSwiss: "Vaud, Valais, Fribourg (septante, huitante, nonante)",
  hyphenLabel: "Hyphens",
  hyphenTraditional: "Traditional (vingt et un, deux cents)",
  hyphenReform: "1990 reform (vingt-et-un, deux-cents)",
  genderLabel: "Noun it counts",
  genderMasculine: "Masculine (un livre)",
  genderFeminine: "Feminine (une maison)",
  secondForTwo: "Write “second” instead of “deuxième” for 2",
  resultHeading: "In French",
  copy: "Copy words",
  copied: "Copied!",
  abbreviationLabel: "Abbreviation",
  otherHyphenLabel: "Other spelling",
  alsoLabel: "Also",
  rulesHeading: "Why it is spelled this way",
  enterPrompt: "Type a number to see it spelled out.",
  errorInvalid:
    "That is not a number. Use digits, with an optional minus sign and decimal comma or point.",
  errorTooBig: "Numbers are limited to 24 digits (below 10²⁴).",
  errorTooLong: "Keep the decimal part to 20 digits or fewer.",
  errorOrdinalRange:
    "Ordinals are supported for whole numbers from 1 up to 24 digits.",
  regionShortFrance: "France",
  regionShortBelgium: "Belgium, Geneva, Neuchâtel, Jura",
  regionShortSwiss: "Vaud, Valais, Fribourg",
  noteEt:
    "“Et” joins the unit 1 to the tens from 21 to 61 and to 71: vingt et un, soixante et onze. It is not used for 81, 91 or after a hundred: quatre-vingt-un, cent un.",
  noteQuatreVingts:
    "Quatre-vingts takes an s because nothing follows it. The s drops when another number does: quatre-vingt-un, quatre-vingt mille.",
  noteQuatreVingtFollowed:
    "Vingt in quatre-vingt takes no s when another number or “mille” follows it.",
  noteCents:
    "Cent takes an s when it is multiplied (deux cents) and nothing follows it, including before million and milliard, which are nouns.",
  noteCentFollowed:
    "Cent takes no s when another number follows it (deux cent un) or when it comes before “mille” (deux cent mille).",
  noteMille: "Mille never takes an s, whatever the multiplier: trois mille.",
  noteLongScale:
    "French uses the long scale: un milliard is 10⁹, un billion is 10¹², un billiard is 10¹⁵ — not the US billion or trillion.",
  noteDe:
    "Million, milliard and the larger names are nouns, so a noun after a round one takes “de”: un million d’euros. Without “de” when other numbers follow: un million deux cent mille euros.",
  noteRegional:
    "The tens 70, 80 and 90 differ by region. The forms for the other regions are listed above.",
} as const;

const DEFAULT_NUMBER = "1999";

type Strings = typeof FRENCH_NUMBER_CONVERTER_STRINGS;
type Mode = "cardinal" | "ordinal";

const NOTE_KEYS: Record<FrenchNote, keyof Strings> = {
  et: "noteEt",
  quatreVingts: "noteQuatreVingts",
  quatreVingtFollowed: "noteQuatreVingtFollowed",
  cents: "noteCents",
  centFollowed: "noteCentFollowed",
  mille: "noteMille",
  longScale: "noteLongScale",
  de: "noteDe",
  regional: "noteRegional",
};

interface Controls {
  mode: Mode;
  region: FrenchRegion;
  hyphenation: FrenchHyphenation;
  gender: FrenchGender;
  secondForTwo: boolean;
}

interface View {
  words: string;
  abbreviation?: string;
  lines: ResultLine[];
  notes: string[];
}

type Outcome = { view: View } | { error: string } | null;

function errorText(error: ReadError, strings: Strings): string | null {
  switch (error) {
    case "empty":
      return null;
    case "tooBig":
      return strings.errorTooBig;
    case "tooLong":
      return strings.errorTooLong;
    default:
      return strings.errorInvalid;
  }
}

function regionName(region: FrenchRegion, strings: Strings): string {
  if (region === "france") {
    return strings.regionShortFrance;
  }
  return region === "belgium"
    ? strings.regionShortBelgium
    : strings.regionShortSwiss;
}

function cardinalView(
  value: DecimalNumber,
  controls: Controls,
  strings: Strings
): View {
  const result = frenchNumber(
    value.negative,
    value.integer,
    value.fraction,
    controls
  );
  const lines: ResultLine[] = result.regionalForms.map((form) => ({
    label: regionName(form.region, strings),
    text: form.words,
    lang: "fr",
  }));
  if (result.otherHyphenation !== null) {
    lines.unshift({
      label: strings.otherHyphenLabel,
      text: result.otherHyphenation,
      lang: "fr",
    });
  }
  return {
    words: result.words,
    lines,
    notes: result.notes.map((note) => strings[NOTE_KEYS[note]]),
  };
}

function ordinalOutcome(
  value: DecimalNumber,
  controls: Controls,
  strings: Strings
): Outcome {
  const ordinal =
    value.negative || value.fraction !== ""
      ? null
      : frenchOrdinal(value.integer, controls);
  if (ordinal === null) {
    return { error: strings.errorOrdinalRange };
  }
  const lines: ResultLine[] = [];
  if (ordinal.otherHyphenation !== null) {
    lines.push({
      label: strings.otherHyphenLabel,
      text: ordinal.otherHyphenation,
      lang: "fr",
    });
  }
  if (ordinal.alternative !== null) {
    lines.push({
      label: strings.alsoLabel,
      text: ordinal.alternative,
      lang: "fr",
    });
  }
  return {
    view: {
      words: ordinal.words,
      abbreviation: ordinal.abbreviation,
      lines,
      notes: [],
    },
  };
}

function evaluate(raw: string, controls: Controls, strings: Strings): Outcome {
  const read = readDecimalNumber(raw);
  if (!read.ok) {
    const message = errorText(read.error, strings);
    return message === null ? null : { error: message };
  }
  if (controls.mode === "ordinal") {
    return ordinalOutcome(read.value, controls, strings);
  }
  return { view: cardinalView(read.value, controls, strings) };
}

export default function FrenchNumberConverterWidget() {
  const strings = useToolUi(FRENCH_NUMBER_CONVERTER_STRINGS);
  const [raw, setRaw] = useState(DEFAULT_NUMBER);
  const [controls, setControls] = useState<Controls>({
    mode: "cardinal",
    region: "france",
    hyphenation: "traditional",
    gender: "masculine",
    secondForTwo: false,
  });
  const set = <K extends keyof Controls>(key: K, value: Controls[K]) =>
    setControls((current) => ({ ...current, [key]: value }));
  const outcome = evaluate(raw, controls, strings);

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.numberHint}
        id="fr-number"
        label={strings.numberLabel}
        numeric
        onChange={setRaw}
        placeholder={strings.numberPlaceholder}
        value={raw}
      />
      <PillGroup
        label={strings.modeLabel}
        name="fr-mode"
        onChange={(value) => set("mode", value)}
        options={[
          { value: "cardinal", label: strings.modeCardinal },
          { value: "ordinal", label: strings.modeOrdinal },
        ]}
        value={controls.mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="fr-region"
          label={strings.regionLabel}
          onChange={(value) => set("region", value)}
          options={[
            { value: "france", label: strings.regionFrance },
            { value: "belgium", label: strings.regionBelgium },
            { value: "swissHuitante", label: strings.regionSwiss },
          ]}
          value={controls.region}
        />
        <SelectField
          id="fr-hyphen"
          label={strings.hyphenLabel}
          onChange={(value) => set("hyphenation", value)}
          options={[
            { value: "traditional", label: strings.hyphenTraditional },
            { value: "reform", label: strings.hyphenReform },
          ]}
          value={controls.hyphenation}
        />
        <SelectField
          id="fr-gender"
          label={strings.genderLabel}
          onChange={(value) => set("gender", value)}
          options={[
            { value: "masculine", label: strings.genderMasculine },
            { value: "feminine", label: strings.genderFeminine },
          ]}
          value={controls.gender}
        />
        {controls.mode === "ordinal" ? (
          <Checkbox
            checked={controls.secondForTwo}
            label={strings.secondForTwo}
            onChange={(value) => set("secondForTwo", value)}
          />
        ) : null}
      </div>
      {outcome === null ? <Notice>{strings.enterPrompt}</Notice> : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <NumberResult
          detail={
            outcome.view.abbreviation
              ? {
                  label: strings.abbreviationLabel,
                  text: outcome.view.abbreviation,
                  lang: "fr",
                }
              : undefined
          }
          lang="fr"
          lines={outcome.view.lines}
          notes={outcome.view.notes}
          strings={{
            heading: strings.resultHeading,
            copy: strings.copy,
            copied: strings.copied,
            rulesHeading: strings.rulesHeading,
          }}
          words={outcome.view.words}
        />
      ) : null}
    </div>
  );
}
